import { useTranslation } from 'react-i18next';
import { Form, Formik, FormikProps } from 'formik';
import { Row, Col } from 'antd';

import { RadioField, InputNumberField, TextareaField, SelectField, InputField } from '../../fields';
import { FondDataFormValues, HazardProfileFormValues } from '../../models/Forms';
import { Button } from '../../components';
import { localeConfig } from '../../config/locale.config';
import { InvestingStrategy } from '../../../features/form/constants';
import { makeFondFormSchema } from './schema';

interface Props {
  onSubmit: (values: FondDataFormValues) => void;
  fondTypeOptions: Array<{ label: string; value: string }>;
  strategy?: { name: string; type: string };
  hazard?: HazardProfileFormValues | null;
  initialValues?: Partial<FondDataFormValues>;
  currency?: string;
}

function FondsForm({ onSubmit, initialValues, strategy, fondTypeOptions, currency = '' }: Props) {
  const { t } = useTranslation();
  const schema = makeFondFormSchema(t);

  function handleOnSubmit(values: FondDataFormValues) {
    onSubmit && onSubmit(values);
  }

  function getFondOptions() {
    return localeConfig.funds
      .filter((fond) => fond.strategy.includes(strategy?.type as InvestingStrategy))
      .map((f) => ({
        label: `${t(f.name)} - ${f.ISIN}`,
        value: `${t(f.name)} - ${f.ISIN}`,
      }));
  }

  function renderForm(formikBag: FormikProps<FondDataFormValues>) {
    return (
      <Form>
        <Row>
          <Col span={24}>
            {strategy ? (
              <SelectField showSearch name="code" options={getFondOptions()} label={t('fondTable.columns.code')} />
            ) : (
              <InputField name="code" label={t('fondTable.columns.code')} />
            )}
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <RadioField name="type" type="horizontal" options={fondTypeOptions} />
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <InputNumberField name="regularAmount" label={`${t('fondTable.columns.regularAmount')} ${currency}`} />
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <InputNumberField name="oneTimeAmount" label={`${t('fondTable.columns.oneTimeAmount')} ${currency}`} />
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <InputNumberField name="agio" label={t('fondTable.columns.agio')} max={100} min={0} />
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <TextareaField name="note" label={t('fondTable.columns.note')} />
          </Col>
        </Row>
        <Button onClick={formikBag.submitForm} type="primary" block>
          {t('fondTable.form.submit')}
        </Button>
      </Form>
    );
  }

  return (
    <Formik<FondDataFormValues>
      initialValues={{
        code: initialValues?.code ?? '',
        type: initialValues?.type ?? 'buy',
        oneTimeAmount: initialValues?.oneTimeAmount ?? 0,
        regularAmount: initialValues?.regularAmount ?? 0,
        agio: initialValues?.agio ?? 0,
        note: initialValues?.note ?? '',
      }}
      onSubmit={handleOnSubmit}
      validationSchema={schema}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {renderForm}
    </Formik>
  );
}

export default FondsForm;
